import { Link } from "react-router-dom";
import { ArrowRight, Award, Building2, Check, Home, Key, MessageCircle, Tag } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useWhatsApp } from "@/lib/siteSettings";
import { SEO } from "@/lib/seo";

type Key = "buy" | "sell" | "rent" | "management" | "citizenship";

const LEARN_ROUTES: Record<Key, string> = {
  buy: "/services/buy",
  sell: "/submit",
  rent: "/services/rent",
  management: "/services/management",
  citizenship: "/citizenship",
};

const ICONS = {
  buy: Home,
  sell: Tag,
  rent: Key,
  management: Building2,
  citizenship: Award,
} as const;

const Services = () => {
  const { t } = useTranslation();
  const { buildWaUrl } = useWhatsApp();

  const services = (["buy", "sell", "rent", "management", "citizenship"] as const).map((key) => ({
    key,
    Icon: ICONS[key],
    title: t(`services.${key}.title`),
    description: t(`services.${key}.description`),
    features: t(`services.${key}.features`, { returnObjects: true }) as string[],
    learnTo: LEARN_ROUTES[key],
    waUrl: buildWaUrl(t(`whatsapp.message.${key}`)),
  }));

  return (
    <div className="bg-background">
      <SEO title={t("seo.services.title")} description={t("seo.services.description")} path="/services" />

      <section className="bg-hero-gradient px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-4xl text-center">
          <h1 className="text-balance text-4xl font-black leading-tight tracking-tight sm:text-5xl">
            {t("services.page.title")}
          </h1>
          <p className="mt-5 text-lg leading-8 text-muted-foreground">{t("services.page.hero")}</p>
        </div>
      </section>

      <section className="px-4 py-14 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-5 sm:grid-cols-2 lg:grid-cols-5">
          {services.map((s) => {
            const Icon = s.Icon;
            return (
              <div
                key={s.key}
                className="flex flex-col rounded-2xl border border-border bg-card p-6 shadow-soft transition hover:-translate-y-1 hover:border-primary/60 hover:shadow-premium"
              >
                <Icon className="h-10 w-10 text-primary" />
                <h3 className="mt-3 text-xl font-black">{s.title}</h3>
                <p className="mt-2 text-sm leading-7 text-muted-foreground">{s.description}</p>
                <ul className="mt-4 space-y-2">
                  {Array.isArray(s.features) &&
                    s.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" /> <span>{f}</span>
                      </li>
                    ))}
                </ul>
                <div className="mt-auto flex flex-col gap-2 pt-5">
                  <a
                    href={s.waUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-full bg-primary px-4 py-2.5 text-sm font-black text-primary-foreground shadow-premium transition hover:scale-[1.02]"
                  >
                    <MessageCircle className="h-4 w-4" /> {t("services.cta.inquireWhatsapp")}
                  </a>
                  <Link
                    to={s.learnTo}
                    className="inline-flex items-center justify-center gap-1 text-sm font-bold text-muted-foreground hover:text-primary"
                  >
                    {t("services.cta.learnMore")} <ArrowRight className="h-3.5 w-3.5 rtl:rotate-180" />
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
};

export default Services;
